import { type ChangeEvent, type SubmitEvent, useMemo, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Briefcase,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { type Division, useDivisions } from '@/hooks/useDivisions';
import { useIsMsp } from '@/hooks/useIsMsp';
// Imported through the feature barrel, not from the hook module: the barrel is
// alias-swapped to a no-op teaser in trial builds, which is what keeps the
// literal `/api/msp` out of the trial bundle (check-trial-bundle.mjs).
import { useDesignateCustomer, useMspCustomers, useUndesignateCustomer } from '@/features/msp';
import { useConfirm } from '@/hooks/useConfirm';
import ConfirmationModal from '@/components/ConfirmationModal';
import ResourceTags from '@/components/ResourceTags';
import { config } from '@/config';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { AppSheet } from '@/components/ui/app-sheet';
import {
  ResponsiveTable,
  type ResponsiveTableColumn,
} from '@/components/ui/responsive-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface CreateDivisionRequest {
  name: string;
  slug: string;
}

interface UpdateDivisionRequest {
  name?: string;
  slug?: string;
}

interface ApiErrorResponse {
  error: string;
  description?: string;
}

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export default function Divisions() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const organizationId = user?.organizationId;

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingDivision, setEditingDivision] = useState<Division | null>(null);
  const [isDeleteConfirmActive, setIsDeleteConfirmActive] = useState(false);
  const [divisionToDelete, setDivisionToDelete] = useState<Division | null>(null);
  const [expandedDivisions, setExpandedDivisions] = useState<Set<string>>(new Set());

  const [formName, setFormName] = useState('');
  const [formSlug, setFormSlug] = useState('');

  const [editFormName, setEditFormName] = useState('');
  const [editFormSlug, setEditFormSlug] = useState('');
  const [isEditSlugManuallyEdited, setIsEditSlugManuallyEdited] = useState(false);

  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: divisions, isLoading, isError, error } = useDivisions(organizationId);

  // MSP designation. Only fetched when the org actually operates as an MSP —
  // otherwise none of this renders and the request would be a wasted 403.
  const isMsp = useIsMsp();
  const { data: customerList } = useMspCustomers(organizationId, isMsp);
  const customers = customerList?.customers;
  const designate = useDesignateCustomer();
  const undesignate = useUndesignateCustomer();
  const { confirm, confirmDialog } = useConfirm();
  const [customerFilter, setCustomerFilter] = useState<'all' | 'customers' | 'internal'>('all');

  // Keyed lookup rather than a find() per row: the table renders every division.
  const customersByDivision = useMemo(
    () => new Map(isMsp ? (customers ?? []).map((c) => [c.divisionId, c] as const) : []),
    [customers, isMsp],
  );

  const visibleDivisions = useMemo(() => {
    const all = divisions ?? [];
    if (!isMsp || customerFilter === 'all') return all;
    return all.filter((d) =>
      customerFilter === 'customers'
        ? customersByDivision.has(d.id)
        : !customersByDivision.has(d.id),
    );
  }, [divisions, isMsp, customerFilter, customersByDivision]);

  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  const createMutation = useMutation<void, AxiosError<ApiErrorResponse>, CreateDivisionRequest>({
    mutationFn: async (next) => {
      await axios.post(`${config.backendApi}/api/divisions`, next, { withCredentials: true });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['divisions'] });
      void queryClient.invalidateQueries({ queryKey: ['workflow', 'onboarding'] });
      setIsCreateOpen(false);
      setFormName('');
      setFormSlug('');
      setSubmitError(null);
    },
    onError: (err) => {
      setSubmitError(
        err.response?.data.description ?? err.response?.data.error ?? 'Failed to create division.',
      );
    },
  });

  const updateMutation = useMutation<
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    void,
    AxiosError<ApiErrorResponse>,
    { id: string; data: UpdateDivisionRequest }
  >({
    mutationFn: async ({ id, data }) => {
      await axios.put(`${config.backendApi}/api/divisions/${id}`, data, {
        withCredentials: true,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['divisions'] });
      setIsEditOpen(false);
      setEditingDivision(null);
      setEditFormName('');
      setEditFormSlug('');
      setIsEditSlugManuallyEdited(false);
      setSubmitError(null);
    },
    onError: (err) => {
      setSubmitError(
        err.response?.data.description ?? err.response?.data.error ?? 'Failed to update division.',
      );
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  const deleteMutation = useMutation<void, AxiosError<ApiErrorResponse>, string>({
    mutationFn: async (id) => {
      await axios.delete(`${config.backendApi}/api/divisions/${id}`, {
        withCredentials: true,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['divisions'] });
      setSubmitError(null);
    },
    onError: (err) => {
      setSubmitError(
        err.response?.data.description ?? err.response?.data.error ?? 'Failed to delete division.',
      );
    },
  });

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setFormName(newName);
    setFormSlug(generateSlug(newName));
  };

  const handleEditNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setEditFormName(newName);
    if (!isEditSlugManuallyEdited) {
      setEditFormSlug(generateSlug(newName));
    }
  };

  const handleEditSlugChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEditFormSlug(e.target.value);
    setIsEditSlugManuallyEdited(true);
  };

  const handleCreate = (e: SubmitEvent) => {
    e.preventDefault();
    createMutation.mutate({ name: formName, slug: formSlug });
  };

  const handleUpdate = (e: SubmitEvent) => {
    e.preventDefault();
    if (editingDivision) {
      updateMutation.mutate({
        id: editingDivision.id,
        data: { name: editFormName, slug: editFormSlug },
      });
    }
  };

  const toggleExpansion = (divisionId: string) => {
    setExpandedDivisions((prev) => {
      const next = new Set(prev);
      if (next.has(divisionId)) {
        next.delete(divisionId);
      } else {
        next.add(divisionId);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Skeleton className="h-9 w-48 mb-6" />
        <Skeleton className="h-64 w-full" />
      </PageContainer>
    );
  }

  if (isError) {
    return (
      <PageContainer>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Error loading divisions: {error.message}</AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  const columns: readonly ResponsiveTableColumn<Division>[] = [
    {
      key: 'expand',
      label: '',
      hideAt: 'mobile',
      render: (d) => {
        const expanded = expandedDivisions.has(d.id);
        return (
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              toggleExpansion(d.id);
            }}
            aria-label={expanded ? 'Collapse details' : 'Expand details'}
          >
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </Button>
        );
      },
    },
    {
      key: 'name',
      label: 'Name',
      hideMobileLabel: true,
      render: (d) => {
        const customer = customersByDivision.get(d.id);
        return (
          <span className="inline-flex items-center gap-2">
            <span className="font-medium">{d.name}</span>
            {customer ? (
              <Badge
                className="border-0 font-normal capitalize gap-1"
                // The lifecycle value carries its own colour from the tag catalog,
                // so an MSP that renames or recolours a state sees that here.
                style={
                  customer.lifecycleColor
                    ? { backgroundColor: `${customer.lifecycleColor}20`, color: customer.lifecycleColor }
                    : undefined
                }
              >
                <Briefcase className="w-3 h-3" />
                {customer.lifecycleDisplayName ?? customer.lifecycle ?? 'Customer'}
              </Badge>
            ) : null}
          </span>
        );
      },
    },
    {
      key: 'created',
      label: 'Created',
      hideAt: 'mobile',
      render: (d) =>
        d.createdAt ? (
          <span
            className="text-xs text-muted-foreground"
            title={new Date(d.createdAt).toLocaleString()}
          >
            {formatDistanceToNow(new Date(d.createdAt), { addSuffix: true })}
          </span>
        ) : null,
    },
    {
      key: 'updated',
      label: 'Updated',
      hideAt: 'mobile',
      render: (d) =>
        d.updatedAt ? (
          <span
            className="text-xs text-muted-foreground"
            title={new Date(d.updatedAt).toLocaleString()}
          >
            {formatDistanceToNow(new Date(d.updatedAt), { addSuffix: true })}
          </span>
        ) : null,
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Divisions"
        subtitle="Carve the organization into sub-groups for delegated access and reporting."
        primary={
          <Button onClick={() => {
            setFormName('');
            setFormSlug('');
            setSubmitError(null);
            setIsCreateOpen(true);
          }}>
            <Plus className="w-4 h-4" />
            Add division
          </Button>
        }
      />

      {submitError ? (
        <Alert variant="destructive" className="mb-4 relative">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{submitError}</AlertDescription>
          <button
            type="button"
            onClick={() => { setSubmitError(null); }}
            className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </Alert>
      ) : null}

      {isMsp ? (
        <div className="flex items-center gap-2 mb-3" role="group" aria-label="Filter divisions">
          {(['all', 'customers', 'internal'] as const).map((value) => (
            <Button
              key={value}
              size="sm"
              variant={customerFilter === value ? 'default' : 'outline'}
              onClick={() => { setCustomerFilter(value); }}
              aria-pressed={customerFilter === value}
            >
              {value === 'all' ? 'All' : value === 'customers' ? 'Customers' : 'Internal'}
            </Button>
          ))}
        </div>
      ) : null}

      <ResponsiveTable<Division>
        columns={columns}
        rows={visibleDivisions}
        rowKey={(r) => r.id}
        rowActions={(division) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => { e.stopPropagation(); }}
                aria-label={`Actions for ${division.name}`}
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => { e.stopPropagation(); }}>
              <DropdownMenuItem
                onClick={() => {
                  setEditingDivision(division);
                  setEditFormName(division.name);
                  setEditFormSlug(division.slug);
                  setIsEditSlugManuallyEdited(false);
                  setIsEditOpen(true);
                  setSubmitError(null);
                }}
                className="gap-2"
              >
                <Pencil className="w-4 h-4" />
                Edit
              </DropdownMenuItem>
              {isMsp ? (
                <DropdownMenuItem
                  disabled={designate.isPending || undesignate.isPending}
                  onClick={() => {
                    if (customersByDivision.has(division.id)) {
                      void confirm({
                        title: `Remove ${division.name} from customers?`,
                        message:
                          'This only removes the customer designation. The division, its regions, devices, tags and commercial record are all kept, and you can designate it again at any time.',
                        confirmText: 'Remove designation',
                      }).then((ok) => {
                        if (ok) undesignate.mutate(division.id);
                      });
                    } else {
                      designate.mutate({ divisionId: division.id });
                    }
                  }}
                  className="gap-2"
                >
                  <Briefcase className="w-4 h-4" />
                  {customersByDivision.has(division.id) ? 'Remove from customers' : 'Make a customer'}
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem
                onClick={() => {
                  setDivisionToDelete(division);
                  setIsDeleteConfirmActive(true);
                }}
                className="gap-2 text-destructive focus:text-destructive"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        empty={<EmptyState variant="no-data" title="No divisions yet" />}
      />

      {visibleDivisions
        .filter((d) => expandedDivisions.has(d.id))
        .map((division) => (
          <div
            key={`exp-${division.id}`}
            className="mt-3 border border-border rounded-lg bg-bg-subtle p-4"
          >
            <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">
              {division.name}
            </div>
            <ResourceTags resourceType="division" resourceId={division.id} />
          </div>
        ))}

      {/* Create modal */}
      <AppSheet open={isCreateOpen} onOpenChange={setIsCreateOpen} title="Create new division">
        <AppSheet.Body>
          <form id="division-create-form" onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="division-name">Name</Label>
              <Input
                id="division-name"
                value={formName}
                onChange={handleNameChange}
                placeholder="Division name"
                required
                autoFocus
              />
            </div>
            <input type="hidden" name="slug" value={formSlug} />
            {submitError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            ) : null}
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            onClick={() => { setIsCreateOpen(false); }}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="division-create-form"
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating…' : 'Create division'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      {/* Edit modal */}
      <AppSheet
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
        title={`Edit division${editingDivision ? `: ${editingDivision.name}` : ''}`}
      >
        <AppSheet.Body>
          <form id="division-edit-form" onSubmit={handleUpdate} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="division-edit-name">Name</Label>
              <Input
                id="division-edit-name"
                value={editFormName}
                onChange={handleEditNameChange}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="division-edit-slug">Slug</Label>
              <Input
                id="division-edit-slug"
                value={editFormSlug}
                onChange={handleEditSlugChange}
                placeholder="division-name-slug"
                required
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                A unique identifier for the division, used in URLs. Auto-generated from name,
                but can be customized.
              </p>
            </div>
            {submitError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            ) : null}
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            onClick={() => { setIsEditOpen(false); }}
            disabled={updateMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="division-edit-form"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Updating…' : 'Update division'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      {confirmDialog}

      <ConfirmationModal
        isActive={isDeleteConfirmActive}
        title="Delete division"
        message={`Are you sure you want to delete the division "${divisionToDelete?.name ?? 'Unknown'}"? This action cannot be undone and may affect regions, devices, gateways, and credentials associated with this division.`}
        confirmText="Delete division"
        cancelText="Cancel"
        onConfirm={() => {
          if (divisionToDelete) {
            deleteMutation.mutate(divisionToDelete.id);
            setIsDeleteConfirmActive(false);
            setDivisionToDelete(null);
          }
        }}
        onCancel={() => {
          setIsDeleteConfirmActive(false);
          setDivisionToDelete(null);
        }}
        isLoading={deleteMutation.isPending}
        isDanger
      />
    </PageContainer>
  );
}
